import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AtSign,
  Download,
  FileText,
  Loader2,
  MessageCircle,
  Paperclip,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import epcProjectService from '../../../services/epcProjectService';
import type {
  EpcProjectMembershipRow,
  ScheduleTaskMessageAttachment,
  ScheduleTaskMessageType,
} from '../../../services/epcProjectService';
import { twMerge } from 'tailwind-merge';

export type ScheduleTaskMessage = {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  messageType?: ScheduleTaskMessageType;
  extensionRequestId?: string;
  mentionedUserIds: string[];
  attachments?: ScheduleTaskMessageAttachment[];
  createdAt: string;
};

const MAX_ATTACHMENTS_PER_MESSAGE = 10;
const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

function formatFileSize(bytes?: number): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let val = bytes;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i += 1;
  }
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

type EpcScheduleTaskMessagesModalProps = {
  open: boolean;
  onClose: () => void;
  projectId: string;
  taskKey: string;
  taskTitle: string;
  rowLabel: string;
  memberships: EpcProjectMembershipRow[];
  currentUserId?: string;
  /** Keeps schedule table badges in sync after load / send / read. */
  onThreadSummarySync?: (taskKey: string, summary: { total: number; unreadFromOthers: number }) => void;
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase() || '?';
}

function formatMsgTime(iso: string): string {
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return '';
    return format(d, 'MMM d · h:mm a');
  } catch {
    return '';
  }
}

export default function EpcScheduleTaskMessagesModal({
  open,
  onClose,
  projectId,
  taskKey,
  taskTitle,
  rowLabel,
  memberships,
  currentUserId,
  onThreadSummarySync,
}: EpcScheduleTaskMessagesModalProps) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState<ScheduleTaskMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [draft, setDraft] = useState('');
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mentionedIds, setMentionedIds] = useState<Set<string>>(() => new Set());
  const [pendingAttachments, setPendingAttachments] = useState<ScheduleTaskMessageAttachment[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [attachmentDownloadingKey, setAttachmentDownloadingKey] = useState<string | null>(null);

  const mentionCandidates = useMemo(() => {
    const q = mentionFilter.trim().toLowerCase();
    return memberships
      .filter((m) => !m.inactive)
      .filter((m) => {
        if (!q) return true;
        const nm = (m.userName || '').toLowerCase();
        const em = (m.userEmail || '').toLowerCase();
        return nm.includes(q) || em.includes(q) || m.userId.toLowerCase().includes(q);
      })
      .slice(0, 12);
  }, [memberships, mentionFilter]);

  const load = useCallback(async () => {
    if (!taskKey || !projectId) return;
    setLoading(true);
    try {
      const data = await epcProjectService.listScheduleTaskMessages(projectId, taskKey, { limit: 80, page: 1 });
      setMessages(
        data.messages.map((m) => ({
          id: m.id,
          authorId: m.authorId,
          authorName: m.authorName,
          body: m.body,
          ...(m.messageType ? { messageType: m.messageType } : {}),
          ...(m.extensionRequestId ? { extensionRequestId: m.extensionRequestId } : {}),
          mentionedUserIds: m.mentionedUserIds,
          attachments: m.attachments ?? [],
          createdAt: m.createdAt,
        }))
      );
      setTotal(data.total);
      try {
        await epcProjectService.markScheduleTaskMessagesRead(projectId, taskKey);
      } catch {
        /* still treat thread as viewed for UI */
      }
      onThreadSummarySync?.(taskKey, { total: data.total, unreadFromOthers: 0 });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not load discussion';
      toast.error(msg);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, taskKey, onThreadSummarySync]);

  useEffect(() => {
    if (open) {
      void load();
      setDraft('');
      setMentionedIds(new Set());
      setMentionOpen(false);
      setPendingAttachments([]);
    }
  }, [open, load]);

  useLayoutEffect(() => {
    if (!open || !scrollRef.current) return;
    const el = scrollRef.current;
    el.scrollTop = el.scrollHeight;
  }, [open, messages.length, loading]);

  const appendMention = (m: EpcProjectMembershipRow) => {
    const label = (m.userName || m.userEmail || m.userId).trim();
    const insert = `@${label} `;
    setDraft((prev) => `${prev}${insert}`);
    setMentionedIds((prev) => new Set(prev).add(m.userId));
    setMentionOpen(false);
    setMentionFilter('');
    textareaRef.current?.focus();
  };

  const handleFilePick = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    const files = evt.target.files;
    if (!files || files.length === 0) return;
    const slots = MAX_ATTACHMENTS_PER_MESSAGE - pendingAttachments.length;
    if (slots <= 0) {
      toast.error(`Max ${MAX_ATTACHMENTS_PER_MESSAGE} attachments per message`);
      evt.target.value = '';
      return;
    }
    const list = Array.from(files).slice(0, slots);
    setUploadingFiles(true);
    try {
      const uploads = await Promise.all(
        list.map(async (f) => {
          if (f.size > MAX_ATTACHMENT_SIZE_BYTES) {
            throw new Error(`${f.name} exceeds 10 MB limit`);
          }
          return epcProjectService.uploadScheduleTaskMessageAttachment(projectId, taskKey, f);
        })
      );
      setPendingAttachments((prev) => [...prev, ...uploads]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Upload failed';
      toast.error(msg);
    } finally {
      setUploadingFiles(false);
      evt.target.value = '';
    }
  };

  const removePendingAttachment = (fileKey: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.fileKey !== fileKey));
  };

  const downloadAttachment = async (att: ScheduleTaskMessageAttachment) => {
    if (attachmentDownloadingKey) return;
    setAttachmentDownloadingKey(att.fileKey);
    try {
      const data = await epcProjectService.getScheduleTaskMessageAttachmentUrl(projectId, att.fileKey);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not open attachment';
      toast.error(msg);
    } finally {
      setAttachmentDownloadingKey(null);
    }
  };

  const send = async () => {
    const body = draft.trim();
    if ((!body && pendingAttachments.length === 0) || sending) return;
    setSending(true);
    try {
      const created = await epcProjectService.postScheduleTaskMessage(projectId, {
        taskKey,
        body,
        mentionedUserIds: Array.from(mentionedIds),
        attachments: pendingAttachments,
      });
      setMessages((prev) => [
        ...prev,
        {
          id: created.id,
          authorId: created.authorId,
          authorName: created.authorName,
          body: created.body,
          ...(created.messageType ? { messageType: created.messageType } : {}),
          ...(created.extensionRequestId ? { extensionRequestId: created.extensionRequestId } : {}),
          mentionedUserIds: created.mentionedUserIds ?? [],
          attachments: created.attachments ?? [],
          createdAt: created.createdAt,
        },
      ]);
      setTotal((n) => {
        const next = n + 1;
        onThreadSummarySync?.(taskKey, { total: next, unreadFromOthers: 0 });
        return next;
      });
      try {
        await epcProjectService.markScheduleTaskMessagesRead(projectId, taskKey);
      } catch {
        /* ignore */
      }
      setDraft('');
      setMentionedIds(new Set());
      setPendingAttachments([]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Send failed';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/55 p-3 backdrop-blur-[2px] sm:items-center sm:p-6"
          role="presentation"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 28, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            role="dialog"
            aria-modal
            aria-labelledby="epc-task-msg-title"
            className="flex max-h-[min(92vh,800px)] w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-white/10 shadow-[0_24px_80px_-12px_rgba(15,23,42,0.55)]"
            style={{
              background: 'linear-gradient(165deg, #0f172a 0%, #134e4a 42%, #0f172a 100%)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative shrink-0 overflow-hidden border-b border-white/10 px-5 pb-4 pt-5">
              <div
                className="pointer-events-none absolute -right-16 -top-20 h-48 w-48 rounded-full opacity-30 blur-3xl"
                style={{ background: 'radial-gradient(circle, #2dd4bf 0%, transparent 70%)' }}
              />
              <div
                className="pointer-events-none absolute -left-10 top-8 h-32 w-32 rounded-full opacity-25 blur-2xl"
                style={{ background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)' }}
              />
              <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2 text-teal-200/90">
                    <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Task discussion</span>
                  </div>
                  <h2 id="epc-task-msg-title" className="text-lg font-semibold leading-snug text-white">
                    {taskTitle || 'Task'}
                  </h2>
                  <p className="mt-1 text-xs text-slate-300/90">{rowLabel}</p>
                  {!loading && total > 0 ? (
                    <p className="mt-2 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-200">
                      <span className="font-semibold text-teal-200/95">{total}</span>
                      <span className="text-slate-400">
                        message{total === 1 ? '' : 's'} in this thread
                      </span>
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="shrink-0 rounded-xl p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div
              ref={scrollRef}
              className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3"
              style={{ maxHeight: 'min(56vh, 480px)' }}
            >
              {loading ? (
                <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-300">
                  <Loader2 className="h-8 w-8 animate-spin text-teal-300" />
                  <p className="text-sm">Loading conversation…</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-14 text-center">
                  <MessageCircle className="h-10 w-10 text-teal-300/80" strokeWidth={1.5} />
                  <p className="text-sm font-medium text-slate-100">Start the thread</p>
                  <p className="max-w-xs text-xs leading-relaxed text-slate-400">
                    Ask questions, share updates, or @mention teammates. Everyone with access to this project can
                    participate.
                  </p>
                </div>
              ) : (
                messages.map((m) => {
                  const mine = currentUserId && m.authorId === currentUserId;
                  return (
                    <motion.div
                      key={m.id}
                      layout
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={twMerge('flex gap-3', mine ? 'flex-row-reverse' : '')}
                    >
                      <div
                        className={twMerge(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl text-xs font-bold ring-2 ring-white/10',
                          mine
                            ? 'bg-gradient-to-br from-teal-400 to-cyan-600 text-white'
                            : 'bg-gradient-to-br from-slate-600 to-slate-800 text-slate-100'
                        )}
                        aria-hidden
                      >
                        {initials(m.authorName)}
                      </div>
                      <div className={twMerge('min-w-0 max-w-[85%]', mine ? 'items-end text-right' : '')}>
                        <div
                          className={twMerge(
                            'mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0',
                            mine ? 'justify-end' : ''
                          )}
                        >
                          <span className="text-xs font-semibold text-teal-100/95">{m.authorName}</span>
                          <time className="text-[10px] text-slate-500" dateTime={m.createdAt}>
                            {formatMsgTime(m.createdAt)}
                          </time>
                        </div>
                        <div
                          className={twMerge(
                            'rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ring-1',
                            mine
                              ? 'bg-teal-500/25 text-slate-50 ring-teal-400/25'
                              : 'bg-white/10 text-slate-100 ring-white/10'
                          )}
                        >
                          {m.messageType === 'extension_request' || m.messageType === 'extension_decision' ? (
                            <div
                              className={twMerge(
                                'rounded-xl border px-3 py-2',
                                m.messageType === 'extension_request'
                                  ? 'border-amber-300/40 bg-amber-400/10 text-amber-100'
                                  : 'border-emerald-300/40 bg-emerald-400/10 text-emerald-100'
                              )}
                            >
                              <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wider opacity-70">
                                {m.messageType === 'extension_request'
                                  ? 'Timeline extension request'
                                  : 'Extension decision'}
                              </div>
                              <div className="whitespace-pre-wrap break-words text-xs">{m.body}</div>
                            </div>
                          ) : m.body && m.body.trim() ? (
                            <p className="whitespace-pre-wrap break-words">{m.body}</p>
                          ) : null}
                          {m.attachments && m.attachments.length > 0 ? (
                            <div
                              className={twMerge(
                                'flex flex-col gap-1.5',
                                m.body && m.body.trim() ? 'mt-2 border-t border-white/10 pt-2' : ''
                              )}
                            >
                              {m.attachments.map((att) => {
                                const downloading = attachmentDownloadingKey === att.fileKey;
                                const sizeText = formatFileSize(att.size);
                                return (
                                  <button
                                    key={att.fileKey}
                                    type="button"
                                    onClick={() => void downloadAttachment(att)}
                                    disabled={downloading}
                                    className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-left text-xs text-slate-100 transition hover:border-teal-300/40 hover:bg-white/10 disabled:opacity-60"
                                  >
                                    <FileText className="h-4 w-4 shrink-0 text-teal-200" />
                                    <span className="min-w-0 flex-1 truncate">
                                      <span className="block truncate font-medium">{att.fileName}</span>
                                      {sizeText ? (
                                        <span className="block text-[10px] text-slate-400">{sizeText}</span>
                                      ) : null}
                                    </span>
                                    {downloading ? (
                                      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-teal-200" />
                                    ) : (
                                      <Download className="h-3.5 w-3.5 shrink-0 text-teal-200/80 transition group-hover:text-teal-200" />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                          {m.mentionedUserIds.length > 0 ? (
                            <p className="mt-2 border-t border-white/10 pt-2 text-[10px] text-teal-200/80">
                              Tagged {m.mentionedUserIds.length} user{m.mentionedUserIds.length === 1 ? '' : 's'}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            <div className="shrink-0 border-t border-white/10 bg-slate-950/40 px-4 py-4">
              <div className="relative">
                {mentionOpen ? (
                  <div className="absolute bottom-full left-0 right-0 z-10 mb-2 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-slate-900/95 py-1 shadow-xl backdrop-blur-md">
                    <div className="border-b border-white/5 px-3 py-2">
                      <input
                        type="search"
                        value={mentionFilter}
                        onChange={(e) => setMentionFilter(e.target.value)}
                        placeholder="Filter team…"
                        className="w-full rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-white placeholder:text-slate-500 focus:border-teal-500/50 focus:outline-none focus:ring-1 focus:ring-teal-500/30"
                        autoFocus
                      />
                    </div>
                    {mentionCandidates.length === 0 ? (
                      <p className="px-3 py-3 text-center text-xs text-slate-500">No matches</p>
                    ) : (
                      mentionCandidates.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => appendMention(m)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-100 transition hover:bg-white/10"
                        >
                          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-[10px] font-bold">
                            {initials(m.userName || m.userEmail || '?')}
                          </span>
                          <span className="min-w-0 flex-1 truncate">
                            <span className="font-medium">{m.userName || m.userEmail}</span>
                            <span className="ml-2 text-[10px] uppercase text-slate-500">{m.role.replace(/_/g, ' ')}</span>
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                ) : null}

                <textarea
                  ref={textareaRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder="Write a message… (Enter to send, Shift+Enter for newline)"
                  rows={3}
                  disabled={sending}
                  className="w-full resize-none rounded-2xl border border-white/10 bg-white/5 px-3.5 py-3 text-sm text-slate-50 placeholder:text-slate-500 focus:border-teal-400/40 focus:outline-none focus:ring-2 focus:ring-teal-500/20 disabled:opacity-60"
                />
                {pendingAttachments.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {pendingAttachments.map((att) => {
                      const sizeText = formatFileSize(att.size);
                      return (
                        <span
                          key={att.fileKey}
                          className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-teal-300/30 bg-teal-500/10 px-2 py-1 text-[11px] text-teal-100"
                        >
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span className="max-w-[160px] truncate">{att.fileName}</span>
                          {sizeText ? <span className="text-[10px] text-teal-200/70">{sizeText}</span> : null}
                          <button
                            type="button"
                            onClick={() => removePendingAttachment(att.fileKey)}
                            className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded text-teal-100/80 transition hover:bg-white/10 hover:text-white"
                            aria-label={`Remove ${att.fileName}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                ) : null}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => void handleFilePick(e)}
                />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={
                        uploadingFiles || pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE
                      }
                      title={`Attach files (max ${MAX_ATTACHMENTS_PER_MESSAGE}, 10 MB each)`}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-teal-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {uploadingFiles ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Paperclip className="h-3.5 w-3.5" />
                      )}
                      Attach
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMentionOpen((v) => !v);
                        setMentionFilter('');
                      }}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-teal-100 transition hover:bg-white/10"
                    >
                      <AtSign className="h-3.5 w-3.5" />
                      Mention
                    </button>
                    {total > 0 ? (
                      <span className="text-[10px] text-slate-500">
                        {messages.length} of {total} loaded
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    disabled={
                      sending ||
                      uploadingFiles ||
                      (!draft.trim() && pendingAttachments.length === 0)
                    }
                    onClick={() => void send()}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-teal-900/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
